import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { memo, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Text } from '@/lib/v2/components';

import {
  atomActionInfoEmail,
  atomIsActionInfoEmailErrors,
} from '@/modules/CampaignsModule/atoms/actionEmailAtom';
import {
  atomActionTypePath,
  atomHeaderCurrentStep,
  haveMessageAtom,
} from '@/modules/CampaignsModule/atoms/actionsAtom';
import { useActionEmailData } from '@/modules/CampaignsModule/hooks/useActionEmailData';
import {
  EActionTypesPath,
  EStepHeaderAction,
} from '@/modules/CampaignsModule/interfaces/CampaignActions';
import { atomDisableEditRule } from '@/modules/RulesModule/atoms/rules';
import { RulesEmailForm } from '@/modules/RulesModule/components/RulesEmailForm';
import { MessageCardsList } from '@/modules/RulesModule/components/RulesEmailForm/components/MessageCardsList';
import { MessageDisplay } from '@/modules/RulesModule/components/RulesEmailForm/components/MessageDisplay';
import { useRulesEmail } from '@/modules/RulesModule/hooks';
import { useRuleStatus } from '@/modules/RulesModule/hooks/useRuleStatus';

const RulesEmailAction = () => {
  const { t } = useTranslation();
  const { actionId } = useParams();

  const [currentStep, setCurrentStep] = useAtom(atomHeaderCurrentStep);
  const haveMessage = useAtomValue(haveMessageAtom);
  const actionInfoEmail = useAtomValue(atomActionInfoEmail);
  const disableEditRule = useAtomValue(atomDisableEditRule);
  const setActionTypePath = useSetAtom(atomActionTypePath);
  const setHaveErrors = useSetAtom(atomIsActionInfoEmailErrors);

  const { isRuleActive, isRuleInactive } = useRuleStatus();

  const { cardList } = useRulesEmail();
  const { getEmailInfo, loading } = useActionEmailData(Number(actionId));

  const messageHTML = useMemo(() => actionInfoEmail?.content?.message, [actionInfoEmail]);
  const showMessageDisplay = useMemo(() => loading || messageHTML, [messageHTML, loading]);
  const messageDisplayDisabled = useMemo(
    () => disableEditRule || isRuleActive || isRuleInactive,
    [disableEditRule, isRuleActive, isRuleInactive]
  );

  useLayoutEffect(() => {
    if (currentStep !== EStepHeaderAction.FIRST) setCurrentStep(EStepHeaderAction.FIRST);
    setActionTypePath(EActionTypesPath.EMAIL);
    void getEmailInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const errors = ['NonExistentTags', 'NotEnoughTags'];
    setHaveErrors(messageHTML && errors.includes(messageHTML) ? true : false);
  }, [messageHTML, setHaveErrors]);

  return (
    <div className="grid w-full grid-cols-2">
      <div className="my-12 bg-white px-10">
        <RulesEmailForm disableForm={disableEditRule || isRuleActive} loading={loading} />
      </div>
      <div className="bg-emblue-secondary-hover">
        {showMessageDisplay && (
          <MessageDisplay
            disabled={messageDisplayDisabled}
            html={actionInfoEmail?.content}
            isLoading={loading}
          />
        )}

        {!showMessageDisplay && (
          <div className="self-start">
            <div>
              <Text
                className="my-12 text-center"
                color={!haveMessage ? 'primary' : 'alert'}
                fontWeight="medium"
                id="simple-email-create-content-label"
              >
                {t(`CAMPAIGN_ACTIONS_MAIN.CREATE_EMAIL_ACTION.createContent`)}
              </Text>
            </div>
            <div>
              <MessageCardsList cardsList={cardList} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(RulesEmailAction);
